import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { ForgePageLoader } from "@/components/ForgeLoader";
import { useSchoolsEntitlement } from "@/features/schools/entitlement";
import {
  useFeeScheduleFinanceAccess,
  useSchoolAcademicsAccess,
  useSchoolAttendanceAccess,
  useSchoolFinanceOperationsAccess,
  useSchoolHealthAccess,
} from "@/features/finance/access";
import { usePermissions } from "@/hooks/usePermissions";

function SchoolsAccessGate({
  allowed,
  isLoading,
  children,
  redirectTo = "/403",
}: {
  allowed: boolean;
  isLoading: boolean;
  children: ReactNode;
  redirectTo?: string;
}) {
  const location = useLocation();

  if (isLoading) {
    return <ForgePageLoader />;
  }

  if (!allowed) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

function SchoolEntitlementGate({ children }: { children: ReactNode }) {
  const entitlement = useSchoolsEntitlement();
  return (
    <SchoolsAccessGate allowed={entitlement.enabled} isLoading={entitlement.isLoading}>
      {children}
    </SchoolsAccessGate>
  );
}

function useSchoolsModuleAccess() {
  const permissions = usePermissions();
  return {
    allowed: permissions.canAccessModule("schools") || permissions.canAccessModule("childcare"),
    isLoading: permissions.isLoading,
  };
}

export function SchoolChildcareRoute({ children }: { children: ReactNode }) {
  const schools = useSchoolsModuleAccess();
  return (
    <SchoolEntitlementGate>
      <SchoolsAccessGate allowed={schools.allowed} isLoading={schools.isLoading}>
        {children}
      </SchoolsAccessGate>
    </SchoolEntitlementGate>
  );
}

export function SchoolAcademicRoute({ children }: { children: ReactNode }) {
  const academics = useSchoolAcademicsAccess();
  return (
    <SchoolEntitlementGate>
      <SchoolsAccessGate allowed={academics.allowed} isLoading={academics.isLoading}>
        {children}
      </SchoolsAccessGate>
    </SchoolEntitlementGate>
  );
}

export function SchoolFinanceRoute({
  children,
  scope = "operations",
}: {
  children: ReactNode;
  scope?: "operations" | "fee-schedules";
}) {
  const schools = useSchoolsModuleAccess();
  const operations = useSchoolFinanceOperationsAccess();
  const feeSchedules = useFeeScheduleFinanceAccess();
  const finance = scope === "fee-schedules" ? feeSchedules : operations;

  return (
    <SchoolEntitlementGate>
      <SchoolsAccessGate
        allowed={schools.allowed || finance.allowed}
        isLoading={schools.isLoading || finance.isLoading}
      >
        {children}
      </SchoolsAccessGate>
    </SchoolEntitlementGate>
  );
}

export function SchoolAttendanceRoute({ children }: { children: ReactNode }) {
  const attendance = useSchoolAttendanceAccess();
  return (
    <SchoolEntitlementGate>
      <SchoolsAccessGate allowed={attendance.allowed} isLoading={attendance.isLoading}>
        {children}
      </SchoolsAccessGate>
    </SchoolEntitlementGate>
  );
}

export function SchoolHealthRoute({ children }: { children: ReactNode }) {
  const health = useSchoolHealthAccess();
  return (
    <SchoolEntitlementGate>
      <SchoolsAccessGate allowed={health.allowed} isLoading={health.isLoading}>
        {children}
      </SchoolsAccessGate>
    </SchoolEntitlementGate>
  );
}
